<script setup lang="ts">
  import { EllipsisVerticalIcon } from '@heroicons/vue/24/outline'
  import { FileDown, Trash2, MessageCircleMore, Undo2, ChevronDown } from 'lucide-vue-next'
  import CaseStatusSelect from '@/components/app/cases/CaseStatusSelect.vue'
  import CaseIntegrationButtons from '@/components/app/cases/CaseIntegrationButtons.vue'
  import {
    type CustomerCase,
    type Entity,
    EntityTypeEnum,
    StateEnum,
    FeatureEnum
  } from '@/types/generated/types.gen'
  import { computed, ref } from 'vue'
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
  } from '@/components/ui/dropdown-menu'
  import { TooltipProvider, TooltipTrigger, TooltipContent, Tooltip } from '@/components/ui/tooltip'
  import { ROUTES } from '@/router/routes'
  import { useTranslation } from '@/composables/useTranslation'
  import { caseTypesDE, caseTypesEN } from '@/i18n/caseTypes'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import Badge from '@/components/ui/badge/Badge.vue'
  import Button from '@/components/ui/button/Button.vue'
  import FeedbackModal from '@/components/app/cases/feedbackModal.vue'

  interface Props {
    caseData: CustomerCase
  }

  const props = defineProps<Props>()

  const emit = defineEmits<{
    (e: 'statusChange', newStatus: string): void
    (e: 'downloadDocuments'): void
    (e: 'deleteCase'): void
  }>()

  const handleStatusChange = (newStatus: string) => {
    emit('statusChange', newStatus)
  }

  const { t } = useTranslation({
    en: {
      backToAllCases: 'Back to all cases',
      caseOf: 'Case of',
      new: 'NEW',
      backToOldView: 'Switch back to old view',
      newView: 'New view',
      giveFeedback: 'Give Feedback',
      downloadAllDocuments: 'Download all documents',
      deleteCase: 'Delete case',
      ...Object.fromEntries(Object.entries(caseTypesEN).map(([key, value]) => [key, value]))
    },
    de: {
      backToAllCases: 'Zurück zu allen Anfragen',
      caseOf: 'Anfrage von',
      new: 'NEU',
      backToOldView: 'Zurück zur alten Ansicht',
      newView: 'Neues Design',
      giveFeedback: 'Feedback geben',
      downloadAllDocuments: 'Alle Dokumente herunterladen',
      deleteCase: 'Anfrage löschen',
      ...Object.fromEntries(Object.entries(caseTypesDE).map(([key, value]) => [key, value]))
    }
  })

  const formattedCreationDate = computed(() => {
    if (!props.caseData.created_at) return '-'
    const date = new Date(props.caseData.created_at)
    return date
      .toLocaleString('de-DE', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
      .replace(',', ' um')
  })

  const formattedEntity = function (entity: Entity | null): string {
    if (!entity) return '-'
    if (entity.entity_type === EntityTypeEnum.NATURAL_PERSON) {
      const value = `${entity.first_name} ${entity.last_name}`
      return !value || value.trim() === '' ? '-' : value
    } else if (entity.entity_type === EntityTypeEnum.LEGAL_PERSON) {
      let value = `${entity.company_name} ${entity.company_legal_form}`
      if (!value || value.trim() === '') {
        value = `${entity.first_name} ${entity.last_name}`
      }
      return !value || value.trim() === '' ? '-' : value
    } else if (entity.entity_type === EntityTypeEnum.INSTITUTION) {
      let value = `${entity.institution_name}`
      if (!value || value.trim() === '') {
        value = `${entity.first_name} ${entity.last_name}`
      }
      return !value || value.trim() === '' ? '-' : value
    }
    return '-'
  }

  const showFeedbackModal = ref(false)
  const isSwitchBackFeedback = ref(false)

  const handleViewChange = (value: string) => {
    if (value === 'old') {
      // Ask for feedback before switching back
      isSwitchBackFeedback.value = true
      showFeedbackModal.value = true
    } else if (value === 'feedback') {
      // Show regular feedback modal
      isSwitchBackFeedback.value = false
      showFeedbackModal.value = true
    }
    // If 'new' is selected, we're already in the new view, so no action needed
  }

  const handleFeedbackClose = () => {
    showFeedbackModal.value = false
    isSwitchBackFeedback.value = false
  }

  const handleFeedbackSubmitted = (feedbackData: any) => {
    showFeedbackModal.value = false

    // If this was switch back feedback, redirect to old view
    if (isSwitchBackFeedback.value) {
      window.location.href = `/cases/${props.caseData.id}/`
      isSwitchBackFeedback.value = false
    } else {
      // Store in localStorage to remember that feedback was given
      localStorage.setItem('newDesignFeedbackGiven', 'true')
    }
  }
</script>

<template>
  <div
    data-cy="case-detail-header"
    class="flex flex-col items-start gap-2 px-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between
      md:px-0"
  >
    <div class="w-full pt-4">
      <div class="mb-2 flex items-center justify-between gap-x-3">
        <router-link
          :to="ROUTES.APP.CASES.CASE_OVERVIEW"
          class="group inline-flex items-center gap-x-2 text-gray-600 hover:text-purple-600"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="size-5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          <span class="text-sm underline decoration-gray-600 group-hover:decoration-purple-600">
            {{ t('backToAllCases') }}
          </span>
        </router-link>
        <div>
          <DropdownMenu>
            <DropdownMenuTrigger
              class="flex h-8 items-center gap-2 rounded-md border border-gray-300 px-3 py-2 hover:bg-gray-100"
            >
              <Badge
                :value="'new'"
                :title="t('new')"
                class="h-5 overflow-hidden p-1"
              >
                <span class="text-xs">{{ t('new') }}</span>
              </Badge>
              <span>{{ t('newView') }}</span>
              <ChevronDown class="h-4 w-4 opacity-50" />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              class="min-w-[--radix-dropdown-menu-trigger-width]"
              :align="'end'"
            >
              <DropdownMenuItem
                class="cursor-pointer"
                @click="handleViewChange('feedback')"
              >
                <MessageCircleMore class="mr-2 h-4 w-4" />
                <span>{{ t('giveFeedback') }}</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                class="cursor-pointer"
                @click="handleViewChange('old')"
              >
                <Undo2 class="mr-2 h-4 w-4" />
                <span>{{ t('backToOldView') }}</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <div class="w-full">
        <div class="mt-5 flex flex-wrap items-center justify-between gap-2">
          <div class="flex items-center gap-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger as-child>
                  <h1
                    data-cy="case-title"
                    class="max-w-full truncate text-base font-semibold text-gray-800 sm:text-lg md:text-xl"
                  >
                    {{ caseData.case_title }}
                  </h1>
                </TooltipTrigger>
                <TooltipContent
                  variant="secondary"
                  showArrow
                >
                  {{ caseData.case_title }}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <div class="flex flex-wrap items-center gap-2">
              <span
                v-if="caseData.jupus_case_number"
                class="font-base text-sm text-gray-500"
              >
                #{{ caseData.jupus_case_number }}
              </span>
              <CaseStatusSelect
                :id="`case-status-${caseData.uuid}`"
                data-cy="case-status"
                :model-value="caseData.state ?? StateEnum.NEW"
                :compact="true"
                @update:model-value="handleStatusChange"
              />
            </div>
          </div>
          <div class="ml-3 inline-flex items-center gap-x-2">
            <CaseIntegrationButtons :case-data="caseData" />
            <DropdownMenu>
              <DropdownMenuTrigger
                class="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-800 shadow-sm
                  hover:bg-gray-50 focus:bg-gray-100 focus:outline-none disabled:pointer-events-none
                  disabled:opacity-50"
              >
                <EllipsisVerticalIcon class="size-5" />
              </DropdownMenuTrigger>
              <DropdownMenuContent class="min-w-[240px]">
                <DropdownMenuItem
                  data-cy="case-download-documents-button"
                  class="flex cursor-pointer items-center gap-x-2 px-4 py-3 text-sm transition-colors hover:bg-gray-50"
                  @click="$emit('downloadDocuments')"
                >
                  <FileDown class="size-4" />
                  {{ t('downloadAllDocuments') }}
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-cy="case-delete-button"
                  class="flex cursor-pointer items-center gap-x-2 px-4 py-3 text-sm text-red-600 transition-colors
                    hover:bg-red-50"
                  @click="$emit('deleteCase')"
                >
                  <Trash2 class="size-5" />
                  {{ t('deleteCase') }}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- Feedback Modal -->
  <FeedbackModal
    v-if="showFeedbackModal"
    :title="
      isSwitchBackFeedback
        ? 'Warum kehren Sie zum alten Design zurück?'
        : 'Was könnten wir am neuen Design verbessern?'
    "
    description="Bitte teilen Sie uns Ihr Feedback mit. Dies hilft uns sicherzustellen, dass das neue Design allen Ihren Anforderungen entspricht."
    :feature="FeatureEnum.NEW_CASE_PAGE"
    :showThumbsRating="false"
    @close="handleFeedbackClose"
    @submitted="handleFeedbackSubmitted"
  />
</template>
